import { Box, Breadcrumbs, Button, Card, CardContent, CircularProgress, Container, Grid, Link, Tooltip, Typography, useTheme } from "@material-ui/core";
import { FC, useEffect, useReducer, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";
import ChevronRightIcon from "../../icons/ChevronRight";
import * as Yup from "yup";
import FormikForm, { FormikStep } from "src/components/ui-components/FormikForm";
import CustomSkeleton from "src/components/skeleton/Skeleton";
import RadioField from "src/components/ui-components/RadioField";
import CheckboxField from "src/components/ui-components/CheckboxField";
import SMTPSettings from "./SMTPSettings";
import AzureSettings from "./AzureSettings";
import { GetEmailMethodsMaster, GetEmailSettings, InsertUpdateEmailSettings } from "./ConnectionService";
import { EmailMethodsMaster, EmailSettingsConfigResponse } from "./Types";

const defaultForm = {
	EmailMethod: 'smtp',
	UseDefault: true,
	SMTPFromAddress: null,
	SMTPServerURL: null,
	SMTPServerPort: null,
	SMTPUsername: null,
	SMTPPassword: null,
	AzureFromAddress: null,
	AzureTenantId: null,
	AzureClientId: null,
	AzureClientSecret: null
};

const SET_EMAIL_METHOD = "SET_EMAIL_METHOD";
const SET_USE_DEFAULT = "SET_USE_DEFAULT";

const TOOLTIP_CONTENT = (
  <>
    <p>Choosing this will apply the default email settings.</p>
    <p><strong>From Address:</strong> noreply@globatechgroup.com</p>
  </>
);

const emailSettingsReducer = (state: any, action: any) => {
  switch (action.type) {
    case SET_EMAIL_METHOD:
      return { ...state, EmailMethod: action.payload };
    case SET_USE_DEFAULT:
      return { ...state, UseDefault: action.payload };
    default:
      return state;
  }
};

const EmailSettings: FC = () => {
	const [isFetching, setIsFetching] = useState<boolean>(false);
	const [emailMethods, setEmailMethods] = useState<EmailMethodsMaster[]>([]);
	const theme = useTheme();
	const formRef = useRef(null);
	const [state, dispatch] = useReducer(emailSettingsReducer, {
    EmailMethod: defaultForm.EmailMethod,
    UseDefault: defaultForm.UseDefault,
  });

	const resetFormData = (data) => {
    if (formRef && formRef.current) {
      formRef.current.resetForm({
        values: { ...data },
      });
    }
  };

  const handleEmailMethodChange = (emailMethod: string) => {
    dispatch({ type: SET_EMAIL_METHOD, payload: emailMethod });
    formRef?.current?.setFieldValue("EmailMethod", emailMethod);
		if (emailMethod === 'smtp') {
			formRef?.current?.setFieldValue("AzureTenantId", null);
			formRef?.current?.setFieldValue("AzureClientId", null);
			formRef?.current?.setFieldValue("AzureClientSecret", null);
			formRef?.current?.setFieldValue("AzureFromAddress", null);
		} else {
			formRef?.current?.setFieldValue("SMTPServerURL", null);
			formRef?.current?.setFieldValue("SMTPServerPort", null);
			formRef?.current?.setFieldValue("SMTPUsername", null);
			formRef?.current?.setFieldValue("SMTPPassword", null);
			formRef?.current?.setFieldValue("SMTPFromAddress", null);
		}
  };

  const handleUseDefaultChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    dispatch({ type: SET_USE_DEFAULT, payload: checked });
    formRef?.current?.setFieldValue("UseDefault", checked);
		formRef?.current?.setTouched({}, false);
  };

	const submitForm = () => {
		console.log(formRef?.current);
    if (formRef && formRef.current && !formRef.current.isSubmitting) {
      formRef.current.submitForm();
    }
  };

	const getData = async () => {
		setIsFetching(true);
		var emailMethods = await GetEmailMethodsMaster();
		setEmailMethods(emailMethods);

		var emailSettings: EmailSettingsConfigResponse = await GetEmailSettings();
		resetFormData({
			EmailMethod: emailSettings.emailMethod,
			UseDefault: emailSettings.useDefault,
			SMTPFromAddress: emailSettings.smtpFromAddress,
			SMTPServerURL: emailSettings.smtpServerURL,
			SMTPServerPort: emailSettings.port !== 0 ? emailSettings.port : null,
			SMTPUsername: emailSettings.username,
			SMTPPassword: emailSettings.password,
			AzureFromAddress: emailSettings.azureFromAddress,
			AzureTenantId: emailSettings.tenantID,
			AzureClientId: emailSettings.clientID,
			AzureClientSecret: emailSettings.clientSecret,
		});
		dispatch({ type: SET_USE_DEFAULT, payload: emailSettings.useDefault });
		dispatch({ type: SET_EMAIL_METHOD, payload: emailSettings.emailMethod });
		setIsFetching(false);
	};

	useEffect(() => {
		getData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<Helmet>
				<title>Email Settings</title>
			</Helmet>
			<Box sx={{ minHeight: "92vh", pb: 4 }}>
				<Container maxWidth={false}>
					<Box
						sx={{
							position: "sticky",
							top: 0,
							zIndex: 1100,
							opacity: 1,
							width: "100%",
							py: 2,
							backgroundColor: theme.palette.background.default,
						}}
					>
						<Grid
							alignItems="center"
							container
							justifyContent="space-between"
							sx={{ width: "100%", opacity: 1 }}
						>
							<Grid item>
								<Breadcrumbs
									aria-label="breadcrumb"
									separator={<ChevronRightIcon fontSize="small" />}
								>
									<Link
										color="textPrimary"
										component={RouterLink}
										to=""
										variant="h5"
									>
										Administration
									</Link>
									<Typography color="textPrimary" variant="h5">
										Email Settings
									</Typography>
								</Breadcrumbs>
							</Grid>
							<Grid item>
								<Button
									onClick={submitForm}
									variant="contained"
									disabled={formRef?.current?.isSubmitting}
									sx={{
										background: "linear-gradient(to bottom, #072f40, #072f40)",
									}}
								>
									Save
									{formRef?.current?.isSubmitting && ( <CircularProgress color="inherit" thickness={5} size={20} sx={{ ml: 1 }} />)}
								</Button>
							</Grid>
						</Grid>
					</Box>
					<Card>
						<CardContent>
							{isFetching && <CustomSkeleton skeletonType="FormEdit" />}
							<Box sx={{ display: isFetching ? "none" : "" }}>
								<FormikForm
                  innerRef={formRef}
                  initialValues={defaultForm}
                  onSubmit={async (values, { setSubmitting }) => {
										setSubmitting(true);
										const apiValues = {
											UseDefault: values.UseDefault,
											EmailMethod: values.EmailMethod,
											SMTPFromAddress: values.SMTPFromAddress,
											SMTPServerURL: values.SMTPServerURL,
											Port: values.SMTPServerPort,
											Username: values.SMTPUsername,
											Password: values.SMTPPassword,
											AzureFromAddress: values.AzureFromAddress,
											TenantID: values.AzureTenantId,
											ClientID: values.AzureClientId,
											ClientSecret: values.AzureClientSecret
										};
										await InsertUpdateEmailSettings(apiValues);
										setSubmitting(false);
                  }}
                >
									<FormikStep
										validationSchema={Yup.object({
											EmailMethod: Yup.string()
												.nullable()
												.required('Email Method is required'),
											UseDefault: Yup.boolean()
												.nullable()
												.required('Required'),
											SMTPFromAddress: Yup.string()
												.nullable()
												.when(["UseDefault", "EmailMethod"], {
													is: (useDefault: boolean, emailMethod: string) => !useDefault && emailMethod === "smtp",
													then: (schema) => schema.required("From Address is required"),
													otherwise: (schema) => schema.nullable(),
												}),
											SMTPServerURL: Yup.string()
												.nullable()
												.when(["UseDefault", "EmailMethod"], {
													is: (useDefault: boolean, emailMethod: string) => !useDefault && emailMethod === "smtp",
													then: (schema) => schema.required("Server URL is required"),
													otherwise: (schema) => schema.nullable(),
												}),
											SMTPServerPort: Yup.string()
												.nullable()
												.when(["UseDefault", "EmailMethod"], {
													is: (useDefault: boolean, emailMethod: string) => !useDefault && emailMethod === "smtp",
													then: (schema) =>
														schema
															.required("Port is required")
															.matches(/^\d+$/, "Only digits are allowed")
															.test("validValue", "Invalid Port value", (value) =>
																value ? parseInt(value, 10) <= 65535 && parseInt(value, 10) > 0 : true
															),
													otherwise: (schema) => schema.nullable(),
												}),
											SMTPUsername: Yup.string()
												.nullable()
												.when(["UseDefault", "EmailMethod"], {
													is: (useDefault: boolean, emailMethod: string) => !useDefault && emailMethod === "smtp",
													then: (schema) => schema.required("Username is required"),
													otherwise: (schema) => schema.nullable(),
												}),
											SMTPPassword: Yup.string()
												.nullable()
												.when(["UseDefault", "EmailMethod"], {
													is: (useDefault: boolean, emailMethod: string) => !useDefault && emailMethod === "smtp",
													then: (schema) => schema.required("Password is required"),
													otherwise: (schema) => schema.nullable(),
												}),
											AzureFromAddress: Yup.string()
												.nullable()
												.when(["UseDefault", "EmailMethod"], {
													is: (useDefault: boolean, emailMethod: string) => !useDefault && emailMethod === "azure",
													then: (schema) => schema.required("From Address is required"),
													otherwise: (schema) => schema.nullable(),
												}),
											AzureTenantId: Yup.string()
												.nullable()
												.when(["UseDefault", "EmailMethod"], {
													is: (useDefault: boolean, emailMethod: string) => !useDefault && emailMethod === "azure",
													then: (schema) => schema.required("Azure Tenant ID is required"),
													otherwise: (schema) => schema.nullable(),
												}),
											AzureClientId: Yup.string()
												.nullable()
												.when(["UseDefault", "EmailMethod"], {
													is: (useDefault: boolean, emailMethod: string) => !useDefault && emailMethod === "azure",
													then: (schema) => schema.required("Azure Client ID is required"),
													otherwise: (schema) => schema.nullable(),
												}),
											AzureClientSecret: Yup.string()
												.nullable()
												.when(["UseDefault", "EmailMethod"], {
													is: (useDefault: boolean, emailMethod: string) => !useDefault && emailMethod === "azure",
													then: (schema) => schema.required("Azure Client Secret is required"),
													otherwise: (schema) => schema.nullable(),
												}),
										})}
									>
										<Grid container spacing={2}>
    								  <Grid item xs={12}>
												<Grid container spacing={2}>
													<Grid item xs={12} sm={12} md={6} lg={6} xl={6} display='flex' alignItems='center'>
														<Tooltip title={TOOLTIP_CONTENT} arrow>
  													  <span>
  													    <CheckboxField 
  													      name="UseDefault" 
  													      label="Use Default Settings"
  													      onChange={handleUseDefaultChange} 
  													    />
  													  </span>
  													</Tooltip>
													</Grid>
													{!state.UseDefault && (
														<Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
															<RadioField
																row
															  name="EmailMethod"
															  label="Email Method"
															  options={emailMethods}
															  valueKey="EmailMethod"
															  labelKey="EmailMethodLabel"
																onChange={(event) => handleEmailMethodChange(event.target.value)}
															/>
														</Grid>
													)}
												</Grid>
    								  </Grid>
											{!state.UseDefault && (
												<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
													{state.EmailMethod === 'smtp' ? <SMTPSettings /> : <AzureSettings />}
												</Grid>
											)}
    								</Grid>
									</FormikStep>
								</FormikForm>
							</Box>
						</CardContent>
					</Card>
				</Container>
			</Box>
		</>
	);
};

export default EmailSettings;